import { makeStyles } from '@material-ui/styles'
import { isAdmin } from 'app/plugins/infrastructure/components/common/helpers'
import Progress from 'core/components/progress/Progress'
import Grid from 'core/elements/grid'
import Text from 'core/elements/Text'
import useListAction from 'core/hooks/useListAction'
import Theme from 'core/themes/model'
import { routeManager } from 'core/utils/routes'
import { Labels } from 'k8s/components/common/entity/labels-and-annotations/LabelsOrAnnotations'
import OverviewCard from 'openstack/components/vms/vm-details/OverviewCard'
import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { isNilOrEmpty } from 'utils/fp'
import { listVolumes } from '../../volumes/actions'
import { volumesSelector } from '../../volumes/selectors'
import { volumeColumns } from '../../volumes/VolumesListPage'
import { listResmgrHosts } from 'openstack/components/infrastructure/actions'
import { resmgrHostsByIdSelector } from 'openstack/components/infrastructure/selectors'
import VolumeErrorModal from '../../volumes/VolumeErrorModal'

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  overview: {
    display: 'grid',
    gridTemplateColumns: '370px 1fr',
    marginTop: '16px',
    gridGap: '24px',
  },
  column: {
    display: 'grid',
    gridGap: '24px',
    gridAutoFlow: 'row',
    gridAutoRows: 'max-content',
  },
}))

const validColumns = [
  'name',
  'id',
  'status',
  'volume_type',
  'description',
  'hostId',
  'backend',
  'attachedVm',
  'device',
  'snapshots',
]

export default function Overview({ volumeType, loading, loadingVolumes, volumes }) {
  const classes = useStyles({})
  const [errorVolume, setErrorVolume] = useState(null)

  const { loading: loadingResmgrHosts } = useListAction(listResmgrHosts)
  const hostsById = useSelector(resmgrHostsByIdSelector)

  const overviewData = useMemo(
    () =>
      volumeType
        ? [
            {
              key: 'Name',
              value: volumeType?.name,
            },
            {
              key: 'Volume Backend',
              value: volumeType?.extra_specs?.volume_backend_name,
            },
            {
              key: 'Public',
              value: volumeType?.is_public,
              render: (val) => (val ? 'True' : 'False'),
            },
            {
              key: 'Description',
              value: volumeType?.description,
            },
            {
              key: 'Metadata',
              value: volumeType?.extra_specs,
              render: ({ metadata }) => {
                return !isNilOrEmpty(metadata) ? (
                  <Labels labels={metadata} variant="default" showMoreButton ellipsisAt={75} />
                ) : (
                  <Text variant="caption1">No Metadata</Text>
                )
              },
            },
          ]
        : [],
    [volumeType],
  )

  const columns = volumeColumns({
    hostsById,
    isAdmin,
    allTenants: false,
    setErrorVolume,
  }).filter((column) => {
    return validColumns.includes(column.key)
  })

  return (
    <Progress loading={loading}>
      {errorVolume && errorVolume?.status === 'error' && errorVolume?.id && (
        <VolumeErrorModal volume={errorVolume} onClose={() => setErrorVolume(null)} />
      )}
      <div className={classes.overview}>
        <div className={classes.column}>
          <OverviewCard item={volumeType} data={overviewData} icon="coins" />
        </div>
        <div className={classes.column}>
          <Grid
            uniqueIdentifier="id"
            data={volumes || []}
            columns={columns}
            loading={loadingVolumes}
            compact
            label="Volumes"
            showItemsCountInLabel={false}
          />
        </div>
      </div>
    </Progress>
  )
}
